import React from 'react';
import { 
  Form, 
  Input, 
  Radio, 
  Button, 
  Card, 
  Typography, 
  Tag,
  Row,
  Col
} from 'antd';
import { 
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons';
import CreateOptions from './CreateOptions';

const { Title } = Typography;

interface VideoQuiz {
  id: string;
  question: string;
  choices: string[];
  answer: number | number[];
  reason: string;
  allowMultiple?: boolean;
}

interface VideoQuizSectionProps {
  videoQuizzes: VideoQuiz[];
  videoQuizEnabled: boolean;
  onVideoQuizEnabledChange: (enabled: boolean) => void;
  onAddVideoQuiz: () => void;
  onRemoveVideoQuiz: (quizId: string) => void;
  onUpdateQuizQuestion: (quizId: string, question: string) => void;
  onUpdateQuizReason: (quizId: string, reason: string) => void;
  onAddQuizChoice: (quizId: string) => void;
  onRemoveQuizChoice: (quizId: string, choiceIndex: number) => void;
  onUpdateQuizChoice: (quizId: string, choiceIndex: number, value: string) => void;
  onSetQuizAnswer: (quizId: string, answerIndex: number) => void;
  onToggleAllowMultiple: (quizId: string, allowMultiple: boolean) => void;
  title?: string; // 유형별 타이틀
}

const VideoQuizSection: React.FC<VideoQuizSectionProps> = ({
  videoQuizzes,
  videoQuizEnabled,
  onVideoQuizEnabledChange,
  onAddVideoQuiz,
  onRemoveVideoQuiz,
  onUpdateQuizQuestion,
  onUpdateQuizReason,
  onAddQuizChoice,
  onRemoveQuizChoice,
  onUpdateQuizChoice,
  onSetQuizAnswer,
  onToggleAllowMultiple,
  title = '동영상 퀴즈'
}) => {
  return (
    <Card className='pb-5 border-b border-gray-200'>
    <Col span={24}>
        <Title level={4} style={{ marginBottom: '16px' }}>{title}</Title>
      </Col>
      <Col span={12}>
        <Form.Item
          label="동영상 URL"
          name="videoUrl"
          rules={videoQuizEnabled ? [
            { required: true, message: '동영상 URL을 입력해주세요' }
          ] : []}
          validateTrigger="onBlur"
        >
          <Input
            placeholder="동영상 URL을 입력하세요"
            size="large"
          />
        </Form.Item>
      </Col>
      <Col span={12}>
        <Form.Item
          label="동영상 퀴즈 등록 여부"
          name="videoQuiz"
          rules={[
            { required: true, message: '동영상 퀴즈 등록 여부를 선택해주세요' }
          ]}
        >
          <Radio.Group onChange={(e) => {
            onVideoQuizEnabledChange(e.target.value);
            // 등록 선택 시 기본 1개 퀴즈 추가
            if (e.target.value && videoQuizzes.length === 0) {
              onAddVideoQuiz();
            }
          }}>
            <Radio value={true}>등록</Radio>
            <Radio value={false}>미등록</Radio>
          </Radio.Group>
        </Form.Item>
      </Col>

      {videoQuizEnabled && (
        <>
          <section className='flex flex-col gap-4'>
            {/* 동영상 퀴즈 영역 */}
            {videoQuizzes.map((quiz, quizIndex) => (
              <Card key={quiz.id} className='mt-4 !bg-gray-50'>
                {/* 퀴즈 제목 */}
                <Title level={4} style={{ margin: 0 }}>동영상 퀴즈 문항 {quizIndex + 1}</Title>
                {/* 퀴즈 문항 */}
                <Row gutter={24}>
                  <Col span={16}>
                    <Form.Item
                      label="동영상 퀴즈 문항"
                      required
                    >
                      <Input
                        placeholder="동영상 퀴즈 문항을 입력하세요"
                        size="large"
                        value={quiz.question}
                        onChange={(e) => onUpdateQuizQuestion(quiz.id, e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                <Row gutter={24}>
                  <Col span={16}>
                    <Form.Item
                      label="정답 선택 방식"
                      required
                    >
                      <Radio.Group
                        value={quiz.allowMultiple ? 'multi' : 'single'}
                        onChange={(e) => onToggleAllowMultiple(quiz.id, e.target.value === 'multi')}
                      >
                        <Radio.Button value="single">단일 선택</Radio.Button>
                        <Radio.Button value="multi">다중 선택</Radio.Button>
                      </Radio.Group>
                    </Form.Item>
                  </Col>
                </Row>

                {/* 퀴즈 보기 */}
                <CreateOptions
                  quiz={quiz}
                  allowMultiple={!!quiz.allowMultiple}
                  onSetQuizAnswer={onSetQuizAnswer}
                  onUpdateQuizChoice={onUpdateQuizChoice}
                  onRemoveQuizChoice={onRemoveQuizChoice}
                  onAddQuizChoice={onAddQuizChoice}
                />

                {/* 퀴즈 정답 사유 */}
                <Row gutter={24}>
                  <Col span={16}>
                    <Form.Item
                      label="정답사유"
                    >
                      <Input
                        placeholder="정답 사유를 입력하세요"
                        size="large"
                        value={quiz.reason}
                        onChange={(e) => onUpdateQuizReason(quiz.id, e.target.value)}
                      />
                    </Form.Item>
                  </Col>
                </Row>

                {/* 퀴즈 삭제 버튼 */}
                <div className='flex gap-2'>
                  <Button 
                    type="primary" 
                    danger 
                    ghost 
                    icon={<DeleteOutlined />} 
                    size="large"
                    onClick={() => onRemoveVideoQuiz(quiz.id)}
                  >
                    퀴즈 삭제
                  </Button>
                </div>
              </Card>
            ))}
          </section>

          {/* 동영상 퀴즈 추가 버튼 */}
          {videoQuizzes.length < 5 && (
            <div className='mt-4'>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                size="large"
                onClick={onAddVideoQuiz}
              >
                동영상 퀴즈 추가
              </Button>
            </div>
          )}
        </>
      )}
    </Card>
  );
};

export default VideoQuizSection; 